/**
 * src/api/mfa.ts
 * 追加認証 API のクライアント。
 * 通常の request()（api/client.ts）は 401 で /login に飛ばすため、ログイン途中の
 * 画面では使えない。ここでは画面側でエラーコードを見て処理する。
 */

const API_BASE = import.meta.env.VITE_API_BASE ?? 'https://mangaos.studio-nekonomiya.workers.dev/api'

export class MfaApiError extends Error {
  status: number
  code: string
  data: Record<string, any>
  constructor(status: number, data: Record<string, any>) {
    super(data?.error ?? 'エラーが発生しました')
    this.status = status
    this.code = data?.code ?? ''
    this.data = data ?? {}
  }
}

export async function mfaFetch<T = any>(
  path: string, method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' = 'GET', body?: object
): Promise<T> {
  let res: Response
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: body === undefined ? undefined : JSON.stringify(body),
    })
  } catch {
    throw new MfaApiError(0, { error: 'サーバーに接続できませんでした', code: 'NETWORK' })
  }
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new MfaApiError(res.status, data)
  return data as T
}

export type MfaMethod = 'none' | 'passkey' | 'email'

export type PasskeyInfo = {
  id: string
  name: string
  created_at: string
  last_used_at: string | null
  device_type: string | null
  backed_up: boolean
  usable: boolean
}

export type MfaSettings = {
  level: 'full' | 'setup'
  role: 'admin' | 'assistant'
  method: MfaMethod
  passkeys: PasskeyInfo[]
  passkey_available: boolean
  email: { address: string; masked: string; verified: boolean; placeholder: boolean; mfa_available: boolean }
  policy: { admin_passkey_required: boolean; assistant_mfa_required: boolean }
  required: boolean
  setup_required: boolean
  stepup_valid: boolean
}

export type ReauthKind = 'passkey' | 'email' | 'password' | 'recovery_token'

export const METHOD_LABEL: Record<MfaMethod, string> = {
  none: '未設定',
  passkey: 'パスキー',
  email: 'メール（6桁コード）',
}
